import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, Plus, Search, Mail, Phone, Building2, MapPin,
  MoreVertical, Edit, Trash2, Eye, Download, Upload, X,
  ChevronLeft, ChevronRight, Tag, Calendar, Activity, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomersPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [linkedUser, setLinkedUser] = useState(null);
  const [deleteLinked, setDeleteLinked] = useState(false);
  const [checkingLinked, setCheckingLinked] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    source: '',
    notes: '',
    tags: [],
    address: { street: '', city: '', state: '', zip_code: '', country: 'USA' },
    subscription: { plan_name: '', status: 'inactive', monthly_value: 0 }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, []);

  const fetchCustomers = async () => {
    try {
      const url = searchQuery 
        ? `${API_URL}/api/customers/?search=${encodeURIComponent(searchQuery)}`
        : `${API_URL}/api/customers/`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const viewCustomerProfile = (customerId) => {
    navigate(`/crm/customers/${customerId}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers();
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Customer created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchCustomers();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create customer');
      }
    } catch (error) {
      toast.error('Failed to create customer');
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${selectedCustomer.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Customer updated successfully');
        setShowCreateModal(false);
        setIsEditing(false);
        resetForm();
        fetchCustomers();
      } else {
        toast.error('Failed to update customer');
      }
    } catch (error) {
      toast.error('Failed to update customer');
    }
  };

  // Check for linked user before deleting
  const handleDeleteClick = async (customer) => {
    setCustomerToDelete(customer);
    setDeleteLinked(false);
    setLinkedUser(null);
    setCheckingLinked(true);
    
    try {
      const response = await fetch(`${API_URL}/api/customers/${customer.id}/linked-user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.has_linked_user) {
          setLinkedUser(data.user);
        }
      }
    } catch (error) {
      console.error('Error checking linked user:', error);
    } finally {
      setCheckingLinked(false);
    }
    
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!customerToDelete) return;

    try {
      const response = await fetch(
        `${API_URL}/api/customers/${customerToDelete.id}?delete_linked=${deleteLinked}`, 
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success(deleteLinked && linkedUser 
          ? 'Customer and linked user account deleted' 
          : 'Customer deleted');
        fetchCustomers();
        fetchStats();
        setShowDeleteDialog(false);
        setCustomerToDelete(null);
        setLinkedUser(null);
      } else {
        toast.error('Failed to delete customer');
      }
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/export/${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `customers_export.${format}`;
        a.click();
        toast.success('Export successful');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      company: customer.company || '',
      source: customer.source || '',
      notes: customer.notes || '',
      tags: customer.tags || [],
      address: customer.address || { street: '', city: '', state: '', zip_code: '', country: 'USA' },
      subscription: customer.subscription || { plan_name: '', status: 'inactive', monthly_value: 0 }
    });
    setIsEditing(true);
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      source: '',
      notes: '',
      tags: [],
      address: { street: '', city: '', state: '', zip_code: '', country: 'USA' },
      subscription: { plan_name: '', status: 'inactive', monthly_value: 0 }
    });
    setSelectedCustomer(null);
    setIsEditing(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customers</h1>
            <p className="text-gray-600 dark:text-gray-300">Manage your customer relationships</p>
          </div>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleExport('json')}>
                  Export as JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')}>
                  Export as CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Total Customers</p>
                    <p className="text-xl font-bold">{stats.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Activity className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Active Subscriptions</p>
                    <p className="text-xl font-bold">{stats.active_subscriptions}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <span className="text-purple-600 font-bold">$</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Monthly Revenue</p>
                    <p className="text-xl font-bold">${stats.total_mrr?.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <Plus className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">New This Month</p>
                    <p className="text-xl font-bold">{stats.new_this_month}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search customers by name, email, company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-customers"
            />
          </div>
          <Button type="submit">Search</Button>
        </form>

        {/* Customer List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-600"></div>
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 dark:text-gray-400">No customers found</p>
                <Button 
                  className="mt-4" 
                  onClick={() => { resetForm(); setShowCreateModal(true); }}
                >
                  Add Your First Customer
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="customers-table">
                  <thead className="bg-gray-50 border-b dark:border-gray-700">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Subscription</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Source</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {customers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50 dark:bg-gray-800" data-testid={`customer-row-${customer.id}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-600 font-semibold">
                                {customer.first_name?.[0]}{customer.last_name?.[0]}
                              </span>
                            </div>
                            <div>
                              <p 
                                className="font-medium cursor-pointer hover:text-blue-600 hover:underline"
                                onClick={() => viewCustomerProfile(customer.id)}
                              >
                                {customer.first_name} {customer.last_name}
                              </p>
                              {customer.tags?.length > 0 && (
                                <div className="flex gap-1 mt-1">
                                  {customer.tags.slice(0, 2).map((tag, i) => (
                                    <span key={i} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">{tag}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                              <Mail className="w-3 h-3" /> {customer.email}
                            </div>
                            {customer.phone && (
                              <div className="flex items-center gap-1 text-gray-500 mt-1">
                                <Phone className="w-3 h-3" /> {customer.phone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {customer.company ? (
                            <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                              <Building2 className="w-4 h-4" /> {customer.company}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {customer.subscription?.status === 'active' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {customer.subscription.plan_name || 'Active'}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">No subscription</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {customer.source || '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`customer-actions-${customer.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => viewCustomerProfile(customer.id)}>
                                <Eye className="w-4 h-4 mr-2" /> View Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditModal(customer)}>
                                <Edit className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => handleDeleteClick(customer)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Delete Customer
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Are you sure you want to delete <strong>{customerToDelete?.first_name} {customerToDelete?.last_name}</strong> ({customerToDelete?.email})?
              </p>
              
              {checkingLinked && (
                <p className="text-sm text-gray-500">Checking for linked records...</p>
              )}
              
              {linkedUser && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                    This customer has a linked User account:
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {linkedUser.name} - {linkedUser.email} ({linkedUser.role_name})
                  </p>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={deleteLinked}
                      onChange={(e) => setDeleteLinked(e.target.checked)}
                      className="w-4 h-4 rounded border-yellow-400 text-yellow-600 focus:ring-yellow-500"
                    />
                    <span className="text-sm text-yellow-800 dark:text-yellow-200">
                      Also delete from Users
                    </span>
                  </label>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleConfirmDelete}
                disabled={checkingLinked}
              >
                {deleteLinked ? 'Delete Both' : 'Delete Customer Only'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{isEditing ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name *</label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="John"
                    data-testid="customer-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name *</label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Doe"
                    data-testid="customer-last-name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  data-testid="customer-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Company</label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Acme Inc."
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Source</label>
                <select
                  className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                >
                  <option value="">Select source...</option>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="social_media">Social Media</option>
                  <option value="advertisement">Advertisement</option>
                  <option value="cold_call">Cold Call</option>
                  <option value="trade_show">Trade Show</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Address Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium mb-3">Address</h3>
                <div className="space-y-3">
                  <Input
                    placeholder="Street Address"
                    value={formData.address.street}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, street: e.target.value }
                    })}
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      placeholder="City"
                      value={formData.address.city}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, city: e.target.value }
                      })}
                    />
                    <Input
                      placeholder="State"
                      value={formData.address.state}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, state: e.target.value }
                      })}
                    />
                    <Input
                      placeholder="ZIP Code"
                      value={formData.address.zip_code}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, zip_code: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Subscription Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium mb-3">Subscription</h3>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    placeholder="Plan Name"
                    value={formData.subscription.plan_name}
                    onChange={(e) => setFormData({
                      ...formData,
                      subscription: { ...formData.subscription, plan_name: e.target.value }
                    })}
                  />
                  <select
                    className="border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                    value={formData.subscription.status}
                    onChange={(e) => setFormData({
                      ...formData,
                      subscription: { ...formData.subscription, status: e.target.value }
                    })}
                  >
                    <option value="inactive">Inactive</option>
                    <option value="active">Active</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <Input
                    type="number"
                    placeholder="Monthly Value"
                    value={formData.subscription.monthly_value}
                    onChange={(e) => setFormData({
                      ...formData,
                      subscription: { ...formData.subscription, monthly_value: parseFloat(e.target.value) || 0 }
                    })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  className="w-full border rounded-md px-3 py-2 min-h-[100px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button onClick={isEditing ? handleUpdate : handleCreate} data-testid="save-customer-btn">
                {isEditing ? 'Update' : 'Create'} Customer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CustomersPage;
